import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  BellIcon,
  EnvelopeIcon,
  SpeakerWaveIcon,
  ComputerDesktopIcon,
  ClockIcon,
  XCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { adminNotificationService } from "../../services/adminNotificationService";
import { AdminNotificationPreferences } from "../../types";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import { soundService } from "../../services/soundService";

const AdminNotificationPreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState<
    Partial<AdminNotificationPreferences>
  >({});
  const [hasChanges, setHasChanges] = useState(false);
  const [initialPreferences, setInitialPreferences] =
    useState<AdminNotificationPreferences | null>(null);

  // Fetch preferences
  const {
    data: currentPreferences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-notification-preferences"],
    queryFn: () => adminNotificationService.getPreferences(),
  });

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: (prefs: Partial<AdminNotificationPreferences>) =>
      adminNotificationService.updatePreferences(prefs),
    onSuccess: (updatedPreferences) => {
      // Update successful

      // Update sound service with new preferences
      if (
        updatedPreferences.realTimeNotifications?.soundEnabled !== undefined
      ) {
        soundService.setEnabled(
          updatedPreferences.realTimeNotifications.soundEnabled
        );
      }

      // Play success sound
      soundService.playSuccessSound();

      // Invalidate and refetch the query
      queryClient
        .invalidateQueries({
          queryKey: ["admin-notification-preferences"],
        })
        .then(() => {
          // Set hasChanges to false after the query is refetched
          setHasChanges(false);
          // Update initial preferences to the new saved state
          setInitialPreferences(updatedPreferences);
        });
    },
    onError: (error) => {
      // Update failed
    },
  });

  // Reset preferences mutation
  const resetMutation = useMutation({
    mutationFn: () => adminNotificationService.resetPreferences(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-notification-preferences"],
      });
      setHasChanges(false);
    },
  });

  // Initialize preferences when data loads
  useEffect(() => {
    if (currentPreferences) {
      // Initialize preferences

      // Ensure we have a complete preferences object
      const completePreferences = {
        ...currentPreferences,
        emailNotifications: currentPreferences.emailNotifications || {},
        realTimeNotifications: currentPreferences.realTimeNotifications || {},
        notificationFrequency:
          currentPreferences.notificationFrequency || "immediate",
      };

      setPreferences(completePreferences);
      setInitialPreferences(completePreferences);

      // Sync sound service with preferences
      soundService.setEnabled(
        currentPreferences.realTimeNotifications?.soundEnabled || false
      );
    }
  }, [currentPreferences]);

  // Track changes
  useEffect(() => {
    // Change detection effect running

    if (initialPreferences && Object.keys(preferences).length > 0) {
      // Create a normalized comparison by excluding fields that shouldn't be compared
      const normalizeForComparison = (prefs: any) => {
        const normalized = { ...prefs };
        delete normalized._id;
        delete normalized.__v;
        delete normalized.createdAt;
        delete normalized.updatedAt;
        delete normalized.adminId;
        delete normalized.quietHours; // Remove quietHours if it exists
        return normalized;
      };

      const normalizedInitial = normalizeForComparison(initialPreferences);
      const normalizedPrefs = normalizeForComparison(preferences);

      // Normalized comparison

      // Debug specific nested objects

      const hasChanges =
        JSON.stringify(normalizedPrefs) !== JSON.stringify(normalizedInitial);
      setHasChanges(hasChanges);

      // Debug logging for troubleshooting
    }
  }, [preferences, initialPreferences]);

  const handlePreferenceChange = (path: string, value: any) => {
    setPreferences((prev) => {
      // Create a deep copy to ensure nested objects are properly updated
      const newPrefs = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let current = newPrefs;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i] as keyof typeof current]) {
          current[keys[i] as keyof typeof current] = {} as any;
        }
        current = current[keys[i] as keyof typeof current] as any;
      }

      current[keys[keys.length - 1] as keyof typeof current] = value;
      return newPrefs;
    });
  };

  const handleSave = () => {
    updateMutation.mutate(preferences);
  };

  const handleReset = () => {
    if (
      window.confirm(
        "Are you sure you want to reset all notification preferences to defaults?"
      )
    ) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <LoadingSpinner fullScreen text="Loading notification preferences..." />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Preferences
          </h2>
          <p className="text-gray-600 mb-4">
            Failed to load notification preferences.
          </p>
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="btn-primary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 admin-dashboard">
      {/* Header */}
      <motion.header
        className="bg-white border-b border-gray-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Notification Preferences
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {hasChanges && (
                <span className="text-sm text-amber-600 font-medium">
                  Unsaved changes
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  hasChanges && !updateMutation.isPending
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {/* Email Notifications */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <EnvelopeIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Email Notifications
                </h2>
                <p className="text-sm text-gray-600">
                  Configure which events trigger email notifications
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                {
                  key: "newRSVP",
                  label: "New RSVP",
                  description: "Get notified when someone submits a new RSVP",
                },
                {
                  key: "dailySummary",
                  label: "Daily Summary",
                  description: "Receive a daily summary of RSVP activity",
                },
                {
                  key: "weeklySummary",
                  label: "Weekly Summary",
                  description: "Receive a weekly summary of RSVP activity",
                },
              ].map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">{item.label}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        preferences.emailNotifications?.[
                          item.key as keyof typeof preferences.emailNotifications
                        ] || false
                      }
                      onChange={(e) =>
                        handlePreferenceChange(
                          `emailNotifications.${item.key}`,
                          e.target.checked
                        )
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Real-time Notifications */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-100 rounded-lg">
                <BellIcon className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Real-time Notifications
                </h2>
                <p className="text-sm text-gray-600">
                  Configure real-time notifications for active dashboard
                  sessions
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">
                    Enable Real-time Notifications
                  </h3>
                  <p className="text-sm text-gray-600">
                    Receive instant notifications while using the dashboard
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      preferences.realTimeNotifications?.enabled || false
                    }
                    onChange={(e) =>
                      handlePreferenceChange(
                        "realTimeNotifications.enabled",
                        e.target.checked
                      )
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {preferences.realTimeNotifications?.enabled && (
                <>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <SpeakerWaveIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          Sound Notifications
                        </h3>
                        <p className="text-sm text-gray-600">
                          Play sound when notifications arrive
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={
                          preferences.realTimeNotifications?.soundEnabled ||
                          false
                        }
                        onChange={(e) =>
                          handlePreferenceChange(
                            "realTimeNotifications.soundEnabled",
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <ComputerDesktopIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          Desktop Notifications
                        </h3>
                        <p className="text-sm text-gray-600">
                          Show browser desktop notifications
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={
                          preferences.realTimeNotifications
                            ?.desktopNotifications || false
                        }
                        onChange={(e) =>
                          handlePreferenceChange(
                            "realTimeNotifications.desktopNotifications",
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* Notification Frequency */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ClockIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Notification Frequency
                </h2>
                <p className="text-sm text-gray-600">
                  How often you want to receive notifications
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {[
                {
                  value: "immediate",
                  label: "Immediate",
                  description: "Get notifications as soon as they happen",
                },
                {
                  value: "hourly",
                  label: "Hourly",
                  description: "Receive notifications once per hour",
                },
                {
                  value: "daily",
                  label: "Daily",
                  description: "Receive notifications once per day",
                },
              ].map((option) => (
                <label
                  key={option.value}
                  className="flex items-center p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <input
                    type="radio"
                    name="notificationFrequency"
                    value={option.value}
                    checked={preferences.notificationFrequency === option.value}
                    onChange={(e) =>
                      handlePreferenceChange(
                        "notificationFrequency",
                        e.target.value
                      )
                    }
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <h3 className="font-medium text-gray-900">
                      {option.label}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </motion.div>

          {/* Reset Button */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Reset to Defaults
                </h2>
                <p className="text-sm text-gray-600">
                  Reset all notification preferences to their default values
                </p>
              </div>
              <button
                onClick={handleReset}
                disabled={resetMutation.isPending}
                className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetMutation.isPending ? "Resetting..." : "Reset to Defaults"}
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default AdminNotificationPreferencesPage;
